import React, { useState, useEffect } from 'react';
import './LeadCaptureForm.css';

const AdminLeadDashboard = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterTier, setFilterTier] = useState('');
    const [selectedLead, setSelectedLead] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    
    // Fetch leads from API
    useEffect(() => {
        fetchLeads();
    }, [filterStatus, filterTier]);
    
    const fetchLeads = async () => {
        setLoading(true);
        setError(null);
        
        try {
            let url = '/api/admin/leads?';
            if (filterStatus) url += `status=${filterStatus}&`;
            if (filterTier) url += `tier=${filterTier}&`;
            
            const response = await fetch(url, {
                headers: {
                    // Add your auth token here
                    // 'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch leads');
            }
            
            const data = await response.json();
            setLeads(data.leads);
        } catch (err) {
            console.error('Error fetching leads:', err);
            setError('Failed to load leads. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    const updateLeadStatus = async (leadId, newStatus, notes = '') => {
        try {
            const response = await fetch(`/api/admin/leads/${leadId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    // 'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    status: newStatus,
                    internal_notes: notes
                })
            });
            
            if (!response.ok) {
                throw new Error('Failed to update lead');
            }
            
            // Refresh leads list
            fetchLeads();
            alert('Lead status updated successfully');
        } catch (err) {
            console.error('Error updating lead:', err);
            alert('Failed to update lead status');
        }
    };
    
    const generateInvoice = async (leadId) => {
        if (!window.confirm('Generate and send invoice to customer?')) {
            return;
        }
        
        try {
            const response = await fetch(`/api/admin/leads/${leadId}/invoice`, {
                method: 'POST',
                headers: {
                    // 'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to generate invoice');
            }
            
            const data = await response.json();
            alert(`Invoice generated successfully!\nInvoice URL: ${data.invoice_url}`);
            
            // Refresh leads list
            fetchLeads();
        } catch (err) {
            console.error('Error generating invoice:', err);
            alert('Failed to generate invoice');
        }
    };
    
    const markAsPaid = async (leadId) => {
        if (!window.confirm('Mark this lead as paid? This is typically done automatically, only use if payment was received via wire/check.')) {
            return;
        }
        
        try {
            const response = await fetch(`/api/admin/leads/${leadId}/mark-paid`, {
                method: 'POST',
                headers: {
                    // 'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to mark as paid');
            }
            
            alert('Lead marked as paid successfully');
            fetchLeads();
        } catch (err) {
            console.error('Error marking as paid:', err);
            alert('Failed to mark lead as paid');
        }
    };
    
    const activateAccount = async (leadId) => {
        if (!window.confirm('Activate customer account? This will create the customer record and send welcome email.')) {
            return;
        }
        
        try {
            const response = await fetch(`/api/admin/leads/${leadId}/activate`, {
                method: 'POST',
                headers: {
                    // 'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to activate account');
            }
            
            const data = await response.json();
            alert(`Account activated successfully!\nCustomer ID: ${data.customer_id}`);
            fetchLeads();
        } catch (err) {
            console.error('Error activating account:', err);
            alert('Failed to activate account');
        }
    };
    
    const viewLeadDetails = async (leadId) => {
        try {
            const response = await fetch(`/api/admin/leads/${leadId}`, {
                headers: {
                    // 'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch lead details');
            }
            
            const lead = await response.json();
            setSelectedLead(lead);
            setShowDetails(true);
        } catch (err) {
            console.error('Error fetching lead details:', err);
            alert('Failed to load lead details');
        }
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };
    
    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        }).format(price);
    };
    
    const getStatusColor = (status) => {
        const colors = {
            'New': '#3498db',
            'Contacted': '#9b59b6',
            'Invoiced': '#f39c12',
            'Paid': '#27ae60',
            'Active': '#27ae60',
            'Declined': '#95a5a6',
            'Archived': '#95a5a6'
        };
        return colors[status] || '#95a5a6';
    };
    
    return (
        <div style={{ padding: '2rem', background: '#f8f9fa', minHeight: '100vh' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                <h1 style={{ marginBottom: '2rem', color: '#000' }}>Lead Management Dashboard</h1>
                
                {/* Filters */}
                <div style={{ background: 'white', padding: '1.5rem', borderRadius: '10px', marginBottom: '2rem', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#000' }}>Status</label>
                            <select 
                                value={filterStatus} 
                                onChange={(e) => setFilterStatus(e.target.value)}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '5px', border: '1px solid #ddd' }}
                            >
                                <option value="">All Statuses</option>
                                <option value="New">New</option>
                                <option value="Contacted">Contacted</option>
                                <option value="Invoiced">Invoiced</option>
                                <option value="Paid">Paid</option>
                                <option value="Active">Active</option>
                                <option value="Declined">Declined</option>
                                <option value="Archived">Archived</option>
                            </select>
                        </div>
                        
                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#000' }}>Tier</label>
                            <select 
                                value={filterTier} 
                                onChange={(e) => setFilterTier(e.target.value)}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '5px', border: '1px solid #ddd' }}
                            >
                                <option value="">All Tiers</option>
                                <option value="Essential">Essential</option>
                                <option value="Professional">Professional</option>
                                <option value="Enterprise">Enterprise</option>
                            </select>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <button 
                                onClick={fetchLeads}
                                style={{ padding: '0.5rem 1.5rem', background: '#3498db', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                            >
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Error message */}
                {error && <div className="error">{error}</div>}
                
                {/* Loading state */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                        <div className="loading" style={{ display: 'inline-block' }}></div>
                        <p>Loading leads...</p>
                    </div>
                ) : (
                    <>
                        {/* Stats */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                                <div style={{ fontSize: '2rem', fontWeight: '600', color: '#3498db' }}>{leads.length}</div>
                                <div style={{ color: '#000' }}>Total Leads</div>
                            </div>
                            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                                <div style={{ fontSize: '2rem', fontWeight: '600', color: '#f39c12' }}>
                                    {leads.filter(l => l.status === 'Invoiced').length}
                                </div>
                                <div style={{ color: '#000' }}>Pending Payment</div>
                            </div>
                            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                                <div style={{ fontSize: '2rem', fontWeight: '600', color: '#27ae60' }}>
                                    {leads.filter(l => l.status === 'Active').length}
                                </div>
                                <div style={{ color: '#000' }}>Active Customers</div>
                            </div>
                        </div>
                        
                        {/* Leads table */}
                        <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                                        <th style={{ padding: '1rem', textAlign: 'left', color: '#000' }}>Company</th>
                                        <th style={{ padding: '1rem', textAlign: 'left', color: '#000' }}>Contact</th>
                                        <th style={{ padding: '1rem', textAlign: 'left', color: '#000' }}>Tier</th>
                                        <th style={{ padding: '1rem', textAlign: 'left', color: '#000' }}>Price</th>
                                        <th style={{ padding: '1rem', textAlign: 'left', color: '#000' }}>Status</th>
                                        <th style={{ padding: '1rem', textAlign: 'left', color: '#000' }}>Created</th>
                                        <th style={{ padding: '1rem', textAlign: 'left', color: '#000' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: '#000' }}>
                                                No leads found
                                            </td>
                                        </tr>
                                    ) : (
                                        leads.map(lead => (
                                            <tr key={lead.id} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '1rem' }}>
                                                    <div style={{ fontWeight: '500', color: '#000' }}>{lead.company_name}</div>
                                                    <div style={{ fontSize: '0.85rem', color: '#000' }}>{lead.company_size}</div>
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <div style={{ color: '#000' }}>{lead.contact_name}</div>
                                                    <div style={{ fontSize: '0.85rem', color: '#000' }}>{lead.contact_email}</div>
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <span style={{ background: '#e8f4f8', padding: '0.25rem 0.75rem', borderRadius: '5px', color: '#000' }}>
                                                        {lead.tier_requested}
                                                    </span>
                                                    {lead.beta_customer && (
                                                        <div style={{ fontSize: '0.75rem', color: '#f39c12', marginTop: '0.25rem' }}>BETA</div>
                                                    )}
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <div style={{ fontWeight: '500', color: '#000' }}>{formatPrice(lead.quoted_price)}</div>
                                                    <div style={{ fontSize: '0.85rem', color: '#000' }}>{lead.payment_term}</div>
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <span style={{ 
                                                        background: getStatusColor(lead.status),
                                                        color: 'white',
                                                        padding: '0.25rem 0.75rem',
                                                        borderRadius: '5px',
                                                        fontSize: '0.85rem'
                                                    }}>
                                                        {lead.status}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '1rem', fontSize: '0.9rem', color: '#000' }}>
                                                    {formatDate(lead.created_at)}
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                        <button
                                                            onClick={() => viewLeadDetails(lead.id)}
                                                            style={{ padding: '0.25rem 0.75rem', background: '#3498db', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem' }}
                                                        >
                                                            View Details
                                                        </button>
                                                        
                                                        {lead.status === 'New' && (
                                                            <button
                                                                onClick={() => generateInvoice(lead.id)}
                                                                style={{ padding: '0.25rem 0.75rem', background: '#27ae60', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem' }}
                                                            >
                                                                Send Invoice
                                                            </button>
                                                        )}
                                                        
                                                        {lead.status === 'Invoiced' && (
                                                            <button
                                                                onClick={() => markAsPaid(lead.id)}
                                                                style={{ padding: '0.25rem 0.75rem', background: '#f39c12', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem' }}
                                                            >
                                                                Mark Paid
                                                            </button>
                                                        )}
                                                        
                                                        {lead.status === 'Paid' && (
                                                            <button
                                                                onClick={() => activateAccount(lead.id)}
                                                                style={{ padding: '0.25rem 0.75rem', background: '#9b59b6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem' }}
                                                            >
                                                                Activate
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
                
                {/* Lead details modal */}
                {showDetails && selectedLead && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 }}>
                        <div style={{ background: 'white', padding: '2rem', borderRadius: '10px', maxWidth: '600px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
                            <h2 style={{ marginTop: 0, color: '#000' }}>Lead Details</h2>
                            
                            <div style={{ marginBottom: '1rem', color: '#000' }}>
                                <strong>Company:</strong> {selectedLead.company_name}<br />
                                <strong>Domain:</strong> {selectedLead.domain || 'N/A'}<br />
                                <strong>Size:</strong> {selectedLead.company_size}<br />
                                <strong>Industry:</strong> {selectedLead.industry}
                            </div>
                            
                            <div style={{ marginBottom: '1rem', color: '#000' }}>
                                <strong>Contact:</strong> {selectedLead.contact_name}<br />
                                <strong>Email:</strong> {selectedLead.contact_email}<br />
                                <strong>Phone:</strong> {selectedLead.contact_phone || 'N/A'}
                            </div>
                            
                            <div style={{ marginBottom: '1rem', color: '#000' }}>
                                <strong>Regulatory Requirements:</strong><br />
                                {selectedLead.regulatory_requirements.join(', ')}
                            </div>
                            
                            <div style={{ marginBottom: '1rem', color: '#000' }}>
                                <strong>Pain Points:</strong><br />
                                {selectedLead.pain_points.join(', ')}
                            </div>
                            
                            <div style={{ marginBottom: '1rem', color: '#000' }}>
                                <strong>Tier:</strong> {selectedLead.tier_requested}<br />
                                <strong>Payment Term:</strong> {selectedLead.payment_term}<br />
                                <strong>Price:</strong> {formatPrice(selectedLead.quoted_price)}
                                {selectedLead.beta_customer && <span style={{ marginLeft: '0.5rem', background: '#f39c12', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '5px', fontSize: '0.85rem' }}>BETA</span>}
                            </div>
                            
                            {selectedLead.square_invoice_url && (
                                <div style={{ marginBottom: '1rem', color: '#000' }}>
                                    <strong>Invoice:</strong><br />
                                    <a href={selectedLead.square_invoice_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3498db' }}>
                                        View Invoice
                                    </a>
                                </div>
                            )}
                            
                            <div style={{ marginBottom: '1rem', color: '#000' }}>
                                <strong>Internal Notes:</strong><br />
                                {selectedLead.internal_notes || 'No notes'}
                            </div>
                            
                            <button
                                onClick={() => setShowDetails(false)}
                                style={{ padding: '0.75rem 1.5rem', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', width: '100%' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminLeadDashboard;
